<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

    import SummaryScreen from './SummaryScreen.svelte';
    import Button from '../Button.svelte';
    import Panel from '../Layout/Panel.svelte';
    import {primaryColour} from '../styles/theme.js';
    import { abortableGet, get } from '../../utils.js';
    import { sgettext } from '../../i18n.js';

    import ElementDetailDiff from './ElementDetailDiff.svelte';
    import ElementDetail from './ElementDetail.svelte';
    import {
        approvalInfo,
        diffLoading,
        elementSource,
        elementTarget,
        selectedElement,
        targetComments
    } from './diffStore.js';

    export let showContextualFields = false;
    export let sourceTemplateVersionNumber = 0;
    export let targetTemplateVersionNumber = 0;

    let compareContent = null;
    let sourceContent = null;

    let reviewerSummary = null;
    let controlOwnerSummary = null;
    let strategyApproverSummary = null;
    let activityApproverSummary = null;

    let showSummaryScreen = true;

    export let approvalSummaryUrl = `${PATH_PREFIX}/sw/api/builder/activityversion/{0}/approval-summary/`;
    $: duplicateEev = $elementTarget != null && $elementSource != null && $elementTarget.element_element_version_id == $elementSource.element_element_version_id && !$elementTarget.element_instance;

    $: getContent($elementTarget, false);
    $: getContent($elementSource, true);
    $: getSummary();

    $: if($elementSource || $elementTarget && !duplicateEev) {
        showSummaryScreen = false;
    }

    let compareRequest = abortableGet();
    let sourceRequest = abortableGet();
    let compareRequestLoading = false;
    let sourceRequestLoading = false;

    function displaySummaryScreen() {
        showSummaryScreen = true;
    }

    async function getSummary() {
        const response = await get(approvalSummaryUrl);
        if (response.ok) {
            const summary = await response.json();
            reviewerSummary = summary.reviewer_summary;
            controlOwnerSummary = summary.control_owner_summary;
            strategyApproverSummary = summary.strategy_approver_summary;
            activityApproverSummary = summary.activity_approver_summary;
        } else {
            const error = await response.json();
            if (error.hasOwnProperty('error')) {
                errors = error;
            }
        }
    }

    async function getContent(eev, isSourceRequest) {

        if (eev === null || eev === undefined) {
            return null;
        } else {
            if (isSourceRequest && sourceRequestLoading) {
                sourceRequest.abort();
                sourceRequest = abortableGet();
            } else if (!isSourceRequest && compareRequestLoading) {
                compareRequest.abort();
                compareRequest = abortableGet();
            }

            let url = `${PATH_PREFIX}/sw/api/elements_database/element_from_eev/${eev.element_element_version_id}/`;
            if (eev.element_instance) {
                url += `?element_instance=${eev.element_instance.id}`;
            }

            const resp = await sourceRequest.fetch(url);
            if(resp.ok){
                if (isSourceRequest) {
                    sourceRequestLoading = true;
                    sourceContent = await resp.json();
                    sourceRequestLoading = false;
                } else {
                    compareRequestLoading = true;
                    compareContent = await resp.json();
                    compareRequestLoading = false;
                }
                if (sourceContent && compareContent) {
                    getElementElementVersionComments(sourceContent, compareContent);
                }
            }
        }
    }

    async function getElementElementVersionComments(sourceContent, compareContent){
        targetComments.set([]);
        const response = await get(`${PATH_PREFIX}/sw/api/elements_database/elementelementversions/${compareContent?.elementId}/eev_comments/?elementId=${sourceContent?.elementId}&fromVersion=${sourceContent?.version}&toVersion=${compareContent?.version}`);
        if (response.ok) {
            targetComments.set(await response.json());
            if(sourceContent){
                targetComments.set($targetComments.filter(x=> x.object_id != sourceContent.eevId));
            }
        }
    }
</script>

<Panel
  title="{sgettext('$$ELEMENT_CHANGE_DETAIL$$')} {$selectedElement
    ? `- ${$selectedElement.element.name}`
    : ''}"
  headerColour={primaryColour}
  collapsible={false}
>
  <div slot="body" style="height: 100%">
    {#if !showSummaryScreen}
      <div class="text-right mb-2">
        <Button
          type="link"
          style="font-weight: bold; text-decoration: none;"
          on:click={displaySummaryScreen}
        >
          <span slot="icon" class="mr-1"><i class="fa fa-arrow-left" /></span>
          <span slot="text" style="letter-spacing: 1px;"
            >{sgettext('$$BACK_SUMMARY$$')}</span
          >
        </Button>
      </div>
    {/if}

    <div class="wrapper">
      {#if showSummaryScreen && !$diffLoading}
        <div class="diff-content" style="padding: 0 15px;">
          <SummaryScreen
            {reviewerSummary}
            {controlOwnerSummary}
            {strategyApproverSummary}
            {activityApproverSummary}
          />
        </div>
      {/if}

      {#if !showSummaryScreen}
        {#if $elementSource && $elementTarget && !duplicateEev}
          <div class="diff-content justify-content-center">
            <ElementDetailDiff
              elementDetails={sourceContent}
              elementTargetDetails={compareContent}
              linkToEV={true}
              count={duplicateEev
                ? {
                    source: $elementSource.count,
                    target: $elementTarget.count,
                  }
                : $elementSource.count}
              countTarget={$elementTarget.count}
              readOnly={true}
              {sourceTemplateVersionNumber}
              {targetTemplateVersionNumber}
              sourceTemplateStatus={sgettext('$$CURRENT$$')}
              targetTemplateStatus={$approvalInfo.templateStatusDisplay}
              {showContextualFields}
            />
          </div>
        {:else if !$elementSource && $elementTarget && !duplicateEev}
          <div class="diff-content justify-content-center">
            <ElementDetail
              elementDetails={compareContent}
              linkToEV={true}
              count={$elementTarget.count}
              leftSide={false}
              readOnly={true}
              templateVersionNumber={targetTemplateVersionNumber}
              sourceTemplateStatus={$approvalInfo.templateStatusDisplay}
              {showContextualFields}
            />
          </div>
        {:else if $elementSource}
          <div class="diff-content justify-content-center">
            <ElementDetail
              elementDetails={sourceContent}
              linkToEV={true}
              count={duplicateEev
                ? {
                    source: $elementSource.count,
                    target: $elementTarget?.count,
                  }
                : $elementSource.count}
              leftSide={true}
              readOnly={true}
              templateVersionNumber={sourceTemplateVersionNumber}
              sourceTemplateStatus={sgettext('$$CURRENT$$')}
              {showContextualFields}
            />
          </div>
        {/if}
      {/if}
    </div>
  </div>
</Panel>

<style>
  .wrapper {
    display: flex;
    flex-direction: row;
  }

  .diff-content {
    flex: 1;
  }

  .padded {
    padding: 10px;
  }

  .diff-content:nth-child(2) {
    border-left: 2px #9fa6ab solid;
  }

  .diff-content {
    padding: 15px;
  }
</style>
